import { useQuery } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type { MineralType } from 'types/minerals';
import type {
  DcaScenarioResponse,
  AreaConfigResponse,
  ProductionStreamConfig,
  DiFormat,
  QiFormat,
  DurationOption,
} from 'types/dca-scenarios-api/responses';
import type { DcaForecastParams } from 'types/api-responses';
import type {
  StreamDcaParams,
  DeclineModel,
  RampUpModel,
  AllLockStates,
} from './types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AreaConfigPrefillData {
  productionStreamConfigs: ProductionStreamConfig[];
  forecastSettings: {
    durationPeriod: number;
    durationOption: DurationOption;
    qiFormat: QiFormat;
    diFormat: DiFormat;
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Strip leading slash from HATEOAS hrefs so they work with ky's prefixUrl.
 * @param {string} path - The href path.
 * @return {string} The path without a leading slash.
 */
function stripLeadingSlash(path: string): string {
  return path.startsWith('/') ? path.slice(1) : path;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAreaConfigPrefill(
  dcaScenarioId: number,
  typeCurveHeaderId: number | null,
  enabled: boolean = true
) {
  return useQuery<AreaConfigPrefillData, Error>({
    queryKey: ['tc-dca-area-config-prefill', dcaScenarioId, typeCurveHeaderId],
    queryFn: async ({ signal }) => {
      // Step 1: Fetch the DCA scenario to get areaConfigs href + forecast settings
      const scenario = await api
        .get(`dca_scenarios/${dcaScenarioId}`, { signal })
        .json<DcaScenarioResponse>();

      const areaConfigsHref = scenario.resources?.areaConfigs?.href;
      if (!areaConfigsHref) {
        throw new Error('DCA scenario has no area configs resource');
      }

      // Step 2: Fetch area configs filtered by type_curve_header_details_id
      const areaConfigsPath = stripLeadingSlash(areaConfigsHref);
      const areaConfigsResponse = await api
        .get(
          `${areaConfigsPath}?type_curve_header_details_id=${typeCurveHeaderId}`,
          {
            signal,
          }
        )
        .json<{ results: AreaConfigResponse[]; totalCount: number }>();

      const firstAreaConfig = areaConfigsResponse.results[0];
      if (!firstAreaConfig) {
        throw new Error('No area config found for this type curve');
      }

      const streamConfigsHref =
        firstAreaConfig.resources?.productionStreamConfigs?.href;
      if (!streamConfigsHref) {
        throw new Error(
          'Area config has no production stream configs resource'
        );
      }

      // Step 3: Fetch production stream configs
      const streamConfigsResponse = await api
        .get(stripLeadingSlash(streamConfigsHref), { signal })
        .json<{ results: ProductionStreamConfig[]; totalCount: number }>();

      return {
        productionStreamConfigs: streamConfigsResponse.results,
        forecastSettings: {
          durationPeriod: scenario.durationPeriod,
          durationOption: scenario.durationOption,
          qiFormat: scenario.qiFormat,
          diFormat: scenario.diFormat,
        },
      };
    },
    enabled: enabled && !!dcaScenarioId && !!typeCurveHeaderId,
    staleTime: 300_000,
  });
}

// ---------------------------------------------------------------------------
// Mapping utility: ProductionStreamConfig[] → per-stream StreamDcaParams
// ---------------------------------------------------------------------------

/**
 * Finds the base (biased) config for a stream: filters by modelType and picks
 * the one with the smallest maxMonthsOnline — matching the well-page
 * `findStreamConfig` pattern.
 * @param {ProductionStreamConfig[]} configs - All production stream configs.
 * @param {string} stream - The stream name (oil, gas, water).
 * @param {'biased'|'ramp_up'} modelType - The model type to filter by.
 * @return {ProductionStreamConfig|undefined} The matching config, if any.
 */
function findBaseConfig(
  configs: ProductionStreamConfig[],
  stream: string,
  modelType: 'biased' | 'ramp_up' = 'biased'
): ProductionStreamConfig | undefined {
  const matches = configs.filter(
    (c) => c.stream === stream && c.modelType === modelType
  );
  if (matches.length === 0) return undefined;
  return matches.reduce((prev, cur) => {
    return cur.maxMonthsOnline < prev.maxMonthsOnline ? cur : prev;
  });
}

function mapConfigToStreamParams(
  dca: ProductionStreamConfig | undefined,
  rampUp: ProductionStreamConfig | undefined
): StreamDcaParams | null {
  if (!dca) return null;

  const locked = dca.lockedParameters ?? {};

  return {
    dcaModel: dca.model as DeclineModel,
    qi: locked.qi ?? null,
    di: locked.di ?? null,
    bFactor: locked.bFactor ?? null,
    dlim: locked.dlim ?? null,
    ri: locked.ri ?? null,
    tElf: locked.tElf ?? null,
    m: locked.m ?? null,
    rampUpModel: rampUp ? (rampUp.model as RampUpModel) : 'none',
    rampUpQi: rampUp?.lockedParameters?.qi ?? null,
    rampUpN: rampUp?.lockedParameters?.n ?? null,
    rampUpDuration: null,
  };
}

export interface MappedStreamConfigs {
  oil: StreamDcaParams | null;
  gas: StreamDcaParams | null;
  water: StreamDcaParams | null;
}

const STREAMS: MineralType[] = ['oil', 'gas', 'water'];

export function mapProductionStreamConfigs(
  configs: ProductionStreamConfig[]
): MappedStreamConfigs {
  const result = {} as Record<MineralType, StreamDcaParams | null>;

  for (const stream of STREAMS) {
    const dcaConfig = findBaseConfig(configs, stream, 'biased');
    const rampUpConfig = findBaseConfig(configs, stream, 'ramp_up');
    result[stream] = mapConfigToStreamParams(dcaConfig, rampUpConfig);
  }

  return result as MappedStreamConfigs;
}

// ---------------------------------------------------------------------------
// Mapping utility: DcaForecastParams → StreamDcaParams
// ---------------------------------------------------------------------------

/**
 * Maps a DcaForecastParams (from header API's dcaForecastParameters)
 * to the form's StreamDcaParams shape.
 * @param {DcaForecastParams} params - The DCA forecast params from the header API.
 * @return {StreamDcaParams} The mapped stream DCA params for the form.
 */
export function mapDcaForecastParamsToStreamParams(
  params: DcaForecastParams
): StreamDcaParams {
  return {
    dcaModel: params.dca_model as DeclineModel,
    qi: params.qi ?? null,
    di: params.di ?? null,
    bFactor: params.b_factor ?? null,
    dlim: params.dlim ?? null,
    rampUpModel: params.ramp_up
      ? (params.ramp_up.model as RampUpModel)
      : 'none',
    rampUpQi: params.ramp_up?.qi ?? null,
    rampUpN: params.ramp_up?.n ?? null,
    rampUpDuration: params.ramp_up?.t_ramp ?? null,
    ri: null,
    tElf: null,
    m: null,
  };
}

// ---------------------------------------------------------------------------
// Lock state extraction: derive which params should be locked from configs
// ---------------------------------------------------------------------------

/**
 * Extracts initial lock states from production stream configs, mirroring the
 * well-page DCA pattern (`getInitialLockedParameters`).  Any parameter present
 * in the config's `lockedParameters` is marked as locked so regression keeps
 * it fixed.
 * @param {ProductionStreamConfig[]} configs - The production stream configs.
 * @return {AllLockStates} Lock states per stream.
 */
export function extractInitialLockStates(
  configs: ProductionStreamConfig[]
): AllLockStates {
  const result: AllLockStates = {
    oil: { dca: {}, rampUp: {} },
    gas: { dca: {}, rampUp: {} },
    water: { dca: {}, rampUp: {} },
  };

  for (const stream of STREAMS) {
    const dcaConfig = findBaseConfig(configs, stream, 'biased');
    const rampUpConfig = findBaseConfig(configs, stream, 'ramp_up');

    if (dcaConfig && 'lockedParameters' in dcaConfig) {
      result[stream].dca = { ...dcaConfig.lockedParameters };
    }
    if (rampUpConfig && 'lockedParameters' in rampUpConfig) {
      result[stream].rampUp = { ...rampUpConfig.lockedParameters };
    }
  }

  return result;
}
